import React, { useState, useEffect } from 'react'
import { FormPartInterface } from '../../../interfaces'
import { FieldGroup, FormCard, SubmitButton, Field } from '../../reusable'
import { Checkbox } from '../../reusable/checkbox'
import { useClaimData, useUpdateClaimData } from '../../../claimProvider'
import { sectionValidation } from '../../../helpers'
import { Report } from 'notiflix/build/notiflix-report-aio'
import { Loading } from 'notiflix'
import styles from './styles.module.scss'
import responsiveStyles from './responsiveStyles.module.scss'

export const ConfirmDetails = (props: FormPartInterface) => {
    const { isActive, assignGroup, saveValueFunct } = props
    const [isConfirmed, setIsConfirmed] = useState(false)
    const [stepData, setStepData] = useState<any>({})
    const [currentStep, setCurrentStep] = useState(0)
    const [postCurrentStep, setPostCurrentStep] = useState(0)
    const claimData = useClaimData()
    const updateClaim = useUpdateClaimData()

    useEffect(() => {
        if (claimData.details_confirmed === 'yes') {
            setIsConfirmed(true)
            setStepData((prevData: Record<string, any>) => ({
                ...prevData,
                details_confirmed: 'yes',
            }))
        }
    }, [claimData])

    const handleConfirmChange = (e: React.MouseEvent) => {
        const newValue = !isConfirmed
        setIsConfirmed(newValue)
        const saved = saveValueFunct({ field: 'details_confirmed', value: newValue ? 'yes' : 'no' })
        setStepData((prevData: Record<string, any>) => ({
            ...prevData,
            details_confirmed: newValue ? 'yes' : 'no',
        }))
        return saved
    }

    const handleProceed = () => {
        try {
            Loading.standard('Processing your submission...')

            const updatedData = {
                ...claimData,
                ...stepData,
                type: 'webform_SENTINEL_ALTERNATE',
                client_id: claimData.client_id,
            }
            updateClaim(updatedData)

            setTimeout(() => {
                assignGroup('signature')
                setTimeout(() => {
                    Loading.remove()
                }, 1000)
            }, 800)
        } catch (error: any) {
            Loading.remove()
            Report.failure('Error', error.message, 'OK')
        }
    }

    const formatAddress = () => {
        const addressParts = [
            claimData.address_line_one,
            claimData.address_line_two,
            claimData.address_line_three,
            claimData.city,
            claimData.postcode,
        ].filter(Boolean)

        return (
            <>
                {addressParts.map((part, index) => (
                    <React.Fragment key={index}>
                        {part}
                        {index < addressParts.length - 1 && <br />}
                    </React.Fragment>
                ))}
            </>
        )
    }

    const getCurrentStep = () => {
        return isActive === 'confirm' ? (
            <FieldGroup title="Confirm Your Details">
                <FormCard step={currentStep} assignPostStep={setPostCurrentStep} onBack={() => assignGroup('contact')}>
                    <h3>Please Review Your Details</h3>
                    <p>Please check that all the information below is correct before proceeding.</p>
                    <div className={`${styles.card} ${responsiveStyles.card}`}>
                        <div className={`${styles.section} ${responsiveStyles.section}`}>
                            <h4 className={`${styles.sectionHeader} ${responsiveStyles.sectionHeader}`}>
                                Personal Information
                            </h4>

                            <div className={`${styles.field} ${responsiveStyles.field}`}>
                                <span className={`${styles.smallLabel} ${responsiveStyles.smallLabel}`}>Title:</span>
                                <span className={`${styles.boldValue} ${responsiveStyles.boldValue}`}>
                                    {claimData.title || 'Not provided'}
                                </span>
                            </div>

                            <div className={`${styles.field} ${responsiveStyles.field}`}>
                                <span className={`${styles.smallLabel} ${responsiveStyles.smallLabel}`}>
                                    First Name:
                                </span>
                                <span className={`${styles.boldValue} ${responsiveStyles.boldValue}`}>
                                    {claimData.firstname || 'Not provided'}
                                </span>
                            </div>

                            <div className={`${styles.field} ${responsiveStyles.field}`}>
                                <span className={`${styles.smallLabel} ${responsiveStyles.smallLabel}`}>
                                    Last Name:
                                </span>
                                <span className={`${styles.boldValue} ${responsiveStyles.boldValue}`}>
                                    {claimData.lastname || 'Not provided'}
                                </span>
                            </div>

                            <div className={`${styles.field} ${responsiveStyles.field}`}>
                                <span className={`${styles.smallLabel} ${responsiveStyles.smallLabel}`}>
                                    Date of Birth:
                                </span>
                                <span className={`${styles.boldValue} ${responsiveStyles.boldValue}`}>
                                    {claimData.dob || 'Not provided'}
                                </span>
                            </div>
                        </div>

                        <hr className={`${styles.divider} ${responsiveStyles.divider}`} />

                        <div className={`${styles.section} ${responsiveStyles.section}`}>
                            <h4 className={`${styles.sectionHeader} ${responsiveStyles.sectionHeader}`}>Address</h4>
                            <div className={`${styles.field} ${responsiveStyles.field}`}>
                                <span className={`${styles.smallLabel} ${responsiveStyles.smallLabel}`}>Address:</span>
                                <span className={`${styles.boldValue} ${responsiveStyles.boldValue}`}>
                                    {formatAddress()}
                                </span>
                            </div>
                        </div>

                        <hr className={`${styles.divider} ${responsiveStyles.divider}`} />

                        <div className={`${styles.section} ${responsiveStyles.section}`}>
                            <h4 className={`${styles.sectionHeader} ${responsiveStyles.sectionHeader}`}>
                                Contact Information
                            </h4>
                            <div className={`${styles.field} ${responsiveStyles.field}`}>
                                <span className={`${styles.smallLabel} ${responsiveStyles.smallLabel}`}>Email:</span>
                                <span className={`${styles.boldValue} ${responsiveStyles.boldValue}`}>
                                    {claimData.email || 'Not provided'}
                                </span>
                            </div>

                            <div className={`${styles.field} ${responsiveStyles.field}`}>
                                <span className={`${styles.smallLabel} ${responsiveStyles.smallLabel}`}>Phone:</span>
                                <span className={`${styles.boldValue} ${responsiveStyles.boldValue}`}>
                                    {claimData.telephone || 'Not provided'}
                                </span>
                            </div>
                        </div>
                    </div>

                    <Field fieldLabel="">
                        <Checkbox
                            fieldName="details_confirmed"
                            label="By checking this box, I confirm the information i have provided is correct and complete. I also confirm Sentinel Legal Ltd has permission to contact me by telephone, e-mail or post regarding my claim(s)."
                            initialValue={claimData.details_confirmed}
                            saveValue={(data) => {
                                setIsConfirmed(data.value === 'yes')
                                return saveValueFunct(data)
                            }}
                        />
                    </Field>

                    <SubmitButton
                        text="Proceed to Signature"
                        btnType="primary"
                        onClick={handleProceed}
                        disabled={!isConfirmed}
                    />
                </FormCard>
            </FieldGroup>
        ) : (
            <></>
        )
    }

    return getCurrentStep()
}
